import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Send, Loader2, Plus, Edit2, Trash2, Phone, Mail, Package, Search, ChevronRight, RefreshCw } from "lucide-react";
import ViewSelector from "../components/ViewSelector";
import ListView from "../components/ListView";

export default function MensajerosPage() {
  const [mensajeros, setMensajeros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingMensajero, setEditingMensajero] = useState(null);
  const [viewMode, setViewMode] = useState("cards");
  const [searchQuery, setSearchQuery] = useState("");
  const [unificando, setUnificando] = useState(false);
  const { getAuthHeaders, user } = useAuth();

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    is_active: true
  });

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/mensajeros`, getAuthHeaders());
      setMensajeros(res.data);
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({ name: "", phone: "", email: "", is_active: true });
    setEditingMensajero(null);
  };

  const openNewDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (mensajero) => {
    setEditingMensajero(mensajero);
    setFormData({
      name: mensajero.name,
      phone: mensajero.phone || "",
      email: mensajero.email || "",
      is_active: mensajero.is_active
    });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error("El nombre es requerido");
      return;
    }

    try {
      if (editingMensajero) {
        await axios.put(`${API}/mensajeros/${editingMensajero.id}`, formData, getAuthHeaders());
        toast.success("Mensajero actualizado");
      } else {
        await axios.post(`${API}/mensajeros`, formData, getAuthHeaders());
        toast.success("Mensajero creado");
      }
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
  };

  const deleteMensajero = async (id) => {
    if (!window.confirm("¿Eliminar este mensajero?")) return;
    try {
      await axios.delete(`${API}/mensajeros/${id}`, getAuthHeaders());
      toast.success("Mensajero eliminado");
      fetchData();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  // Función para unificar solicitudes de mensajeros duplicados
  const unificarMensajeros = async () => {
    if (!window.confirm("¿Desea unificar las solicitudes de mensajeros con nombres similares (ej: con/sin acentos)?")) return;
    
    setUnificando(true);
    try {
      const res = await axios.post(`${API}/mensajeros/unificar`, {}, getAuthHeaders());
      toast.success(`${res.data.mensaje}`);
      fetchData();
    } catch (err) {
      toast.error("Error al unificar mensajeros");
      console.error(err);
    } finally {
      setUnificando(false);
    }
  };

  // Verificar si es admin
  const isAdmin = () => {
    const role = user?.role?.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "") || "";
    return ["administrador", "admin"].includes(role);
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="mensajeros-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
              Mensajeros
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Gestiona tu equipo de mensajeros
            </p>
          </div>
          <div className="flex gap-2">
            {isAdmin() && (
              <Button 
                onClick={unificarMensajeros} 
                variant="outline"
                className="border-amber-500 text-amber-600 hover:bg-amber-50"
                disabled={unificando}
                title="Unifica solicitudes de mensajeros con nombres similares (ej: con/sin acentos)"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${unificando ? 'animate-spin' : ''}`} />
                {unificando ? "Unificando..." : "Unificar Datos"}
              </Button>
            )}
            <Button onClick={openNewDialog} className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Mensajero
            </Button>
          </div>
        </div>

        {/* Search & View Selector */}
        <div className="flex flex-wrap gap-4 mb-6 items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar mensajeros..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-mensajeros"
            />
          </div>
          <ViewSelector 
            currentView={viewMode} 
            onViewChange={setViewMode} 
            availableViews={['cards', 'list']}
          />
        </div>

        {(() => {
          const filteredMensajeros = mensajeros.filter(m =>
            m.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            m.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            m.phone?.includes(searchQuery)
          );

          if (loading) {
            return (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            );
          }

          if (filteredMensajeros.length === 0) {
            return (
              <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                <CardContent className="py-12 text-center">
                  <Send className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-500 dark:text-slate-400 mb-4">
                    {searchQuery ? "No se encontraron mensajeros" : "No hay mensajeros registrados"}
                  </p>
                  {!searchQuery && (
                    <Button onClick={openNewDialog} variant="outline">
                      <Plus className="w-4 h-4 mr-2" />
                      Agregar primer mensajero
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          }

          if (viewMode === 'list') {
            return (
              <ListView
                items={filteredMensajeros}
                onItemClick={openEditDialog}
                renderItem={(m) => (
                  <>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${m.is_active ? 'bg-green-100 dark:bg-green-900/30' : 'bg-slate-100 dark:bg-slate-700'}`}>
                          <Send className={`w-4 h-4 ${m.is_active ? 'text-green-600' : 'text-slate-400'}`} />
                        </div>
                        <div>
                          <p className="font-medium text-sm text-slate-900 dark:text-white">{m.name}</p>
                          <div className="flex items-center gap-3 text-xs text-slate-500">
                            {m.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {m.phone}</span>}
                            {m.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {m.email}</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 ml-4">
                      <Badge variant="secondary" className="text-xs">
                        <Package className="w-3 h-3 mr-1" />
                        {m.deliveries_count || 0}
                      </Badge>
                      <Badge className={m.is_active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}>
                        {m.is_active ? "Activo" : "Inactivo"}
                      </Badge>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openEditDialog(m); }}>
                          <Edit2 className="w-4 h-4 text-slate-400" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deleteMensajero(m.id); }}>
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </div>
                  </>
                )}
              />
            );
          }

          // Default: Cards view
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredMensajeros.map((m) => (
                <Card key={m.id} className="border-slate-200 dark:border-slate-700 dark:bg-slate-800 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => openEditDialog(m)}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${m.is_active ? 'bg-green-100 dark:bg-green-900/30' : 'bg-slate-100 dark:bg-slate-700'}`}>
                          <Send className={`w-6 h-6 ${m.is_active ? 'text-green-600' : 'text-slate-400'}`} />
                        </div>
                        <div>
                          <CardTitle className="text-lg dark:text-white">{m.name}</CardTitle>
                          <Badge className={m.is_active ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" : "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300"}>
                            {m.is_active ? "Activo" : "Inactivo"}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openEditDialog(m); }}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deleteMensajero(m.id); }} className="text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      {m.phone && (
                        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                          <Phone className="w-4 h-4" />
                          {m.phone}
                        </div>
                      )}
                      {m.email && (
                        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                          <Mail className="w-4 h-4" />
                          {m.email}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                        <Package className="w-4 h-4" />
                        {m.deliveries_count || 0} entregas realizadas
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          );
        })()}

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle className="dark:text-white">
                {editingMensajero ? "Editar Mensajero" : "Nuevo Mensajero"}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="dark:text-slate-300">Nombre *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Nombre completo"
                  className="dark:bg-slate-700 dark:border-slate-600"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="dark:text-slate-300">Teléfono</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="Número de teléfono"
                  className="dark:bg-slate-700 dark:border-slate-600"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="dark:text-slate-300">Email</Label>
                <Input
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="Correo electrónico"
                  className="dark:bg-slate-700 dark:border-slate-600"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label className="dark:text-slate-300">Activo</Label>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(v) => setFormData({...formData, is_active: v})}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
              <Button onClick={handleSubmit} className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white">
                {editingMensajero ? "Actualizar" : "Crear"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
